/**
 * Date range utility functions for analytics widgets
 */

export interface DateRangeParams {
  current_start: string;
  current_end: string;
}

/**
 * Get the "This Week" date range (Monday 00:00:00 UTC to now)
 * Week-to-date approach: shows data from start of week through current moment
 * @returns DateRangeParams with ISO formatted start and end dates
 */
export function getThisWeekDateRange(): DateRangeParams {
  const now = new Date();

  // Calculate Monday of current week
  const startOfWeek = new Date(now);
  const currentDay = now.getDay();
  // If Sunday (0), go back 6 days; otherwise go back currentDay - 1 days
  const daysToMonday = currentDay === 0 ? 6 : currentDay - 1;
  startOfWeek.setDate(now.getDate() - daysToMonday);

  // Set start to Monday 00:00:00 UTC
  const startUTC = new Date(
    Date.UTC(startOfWeek.getFullYear(), startOfWeek.getMonth(), startOfWeek.getDate(), 0, 0, 0)
  );

  // End at current moment (end of today for consistency)
  const endUTC = new Date(
    Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
  );

  return {
    current_start: startUTC.toISOString(),
    current_end: endUTC.toISOString(),
  };
}

/**
 * Get the "Today" date range (00:00:00 UTC to 23:59:59 UTC)
 * @returns DateRangeParams with ISO formatted start and end dates
 */
export function getTodayDateRange(): DateRangeParams {
  const now = new Date();

  const startUTC = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0));
  const endUTC = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999));

  return {
    current_start: startUTC.toISOString(),
    current_end: endUTC.toISOString(),
  };
}

/**
 * Get the "Past N Days" date range
 * @param days Number of days to look back
 * @returns DateRangeParams with ISO formatted start and end dates
 */
export function getPastDaysDateRange(days: number): DateRangeParams {
  const now = new Date();
  const startDate = new Date(now);
  startDate.setDate(now.getDate() - days);

  const startUTC = new Date(
    Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0)
  );
  const endUTC = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999));

  return {
    current_start: startUTC.toISOString(),
    current_end: endUTC.toISOString(),
  };
}

/**
 * Format an OData date range (two ISO strings) into a short, human label.
 *
 * Examples:
 *   ['2026-06-09...', '2026-06-15...'] -> 'From Jun 9 To Jun 15, 2026'
 *   ['2025-12-28...', '2026-01-04...'] -> 'From Dec 28, 2025 To Jan 4, 2026'
 *
 * Returns '' when the range is missing or incomplete.
 */
export function formatDateRangeLabel(range: string[] | null | undefined): string {
  if (!range || range.length < 2) return '';

  const start = new Date(range[0]);
  const end = new Date(range[1]);
  const base: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' };
  const sameYear = start.getFullYear() === end.getFullYear();

  const startStr = start.toLocaleDateString('en-US', sameYear ? base : { ...base, year: 'numeric' });
  const endStr = end.toLocaleDateString('en-US', { ...base, year: 'numeric' });

  return `From ${startStr} To ${endStr}`;
}

/**
 * The calendar day a chart bucket stands for, at local midnight.
 * A bucket such as "2026-10-05T00:00:00.000Z" is a day, not an instant: `new Date()`
 * would shift it to the previous day west of UTC, so the label would name the wrong
 * week, month or year.
 * @param value ISO date or datetime string; only its YYYY-MM-DD part is read
 * @returns Date at 00:00 local time on that day
 */
export function toCalendarDay(value: string): Date {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
}
